import { KeyboardEventHandler, useState } from 'react';

import styled from '@emotion/styled';
import {
  ButtonLoadingAnimation,
  Icons,
  PressableBox,
  Text,
  TextButton,
} from '@opendoor/bricks-next';
import { Box, List, ListItem } from '@opendoor/bricks/core';
import { novo } from '@opendoor/bricks/theme';
import Field from '@opendoor/cinderblocks/growth/novo/inputs/Field';
import { InputContainerElement } from '@opendoor/cinderblocks/growth/novo/inputs/Input';
import { useRouter } from 'next/router';

import {
  OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product,
  OdProtosSellReceptionData_SellerInput_Channel,
} from '__generated__/athena';

import { useT } from 'helpers/i18n';

import { gateManualAddressVerification } from '../../components/shared/NewSellerFlowAddressInput';
import { useAddressValidationContext } from '../../helpers/AddressValidationContext';

interface PredictionTerm {
  offset: number;
  value: string;
}
export interface AutoCompletePrediction {
  description: string;
  place_id?: string;
  addressToken?: string;
  types?: string[];
  source: string;
  terms?: PredictionTerm[];
}

export type NovoAddressInputProps = {
  id: string;
  placeholder?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  prediction: {
    predictions: AutoCompletePrediction[];
    setPredictions: (predictions: AutoCompletePrediction[]) => void;
    searchTerm: string;
    setSearchTerm: (searchTerm: string) => void;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handlePredictionSelect: (prediction: AutoCompletePrediction) => void;
    listBoxId?: string;
  };
  product?: OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product;
  pageSetPath?: string;
  trackingKeywords?: string;
  partnerReferralId?: string;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
};

// Override the field input container styles
// If in the future, we have address entry component in bricks-next, we should use it instead
const CustomFieldWrapper = styled.div<{ noValue: boolean }>`
  ${InputContainerElement} {
    outline: none;
    border: 1.5px solid ${novo.colors.warmgrey300};
    &:hover {
      outline: none;
    }
    &:focus-within {
      outline: none;
      border: 1.5px solid ${novo.colors.brandBlue600};
      border-top-left-radius: ${novo.radii.roundedSmooth};
      border-top-right-radius: ${novo.radii.roundedSmooth};
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-bottom: none;
      ${(props) =>
        props.noValue &&
        `
        border-bottom-left-radius: ${novo.radii.roundedSmooth};
        border-bottom-right-radius: ${novo.radii.roundedSmooth};
        border-bottom: 1.5px solid ${novo.colors.brandBlue600};
      `}
    }
  }
`;

const NovoListBox = ({ children, listBoxId }: { children: React.ReactNode; listBoxId: string }) => {
  return (
    <Box
      borderBottomRadius={'roundedSmooth'}
      maxHeight="450px"
      pt={2}
      position="absolute"
      top={['-11px', null, '-19px', '-27px']}
      w="100%"
      border={'1.5px solid #0040E6'}
      backgroundColor={'white0'}
      overflow={'hidden'}
    >
      <List variant="text" role="listbox" aria-label="addresses" id={listBoxId}>
        {children}
      </List>
    </Box>
  );
};

const AutoCompletePredictions = ({
  predictions,
  selectedIndex,
  onPredictionSelect,
  listBoxId,
  pageSetPath,
  trackingKeywords,
  partnerReferralId,
  channel,
}: {
  predictions: AutoCompletePrediction[];
  selectedIndex?: number;
  onPredictionSelect: (prediction: AutoCompletePrediction) => void;
  listBoxId: string;
  pageSetPath?: string;
  trackingKeywords?: string;
  partnerReferralId?: string;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
}) => {
  const { t } = useT();
  const router = useRouter();
  return (
    <NovoListBox listBoxId={listBoxId}>
      {predictions.map((prediction, index) => {
        const description = prediction.description;
        const place_id = prediction.place_id;
        const address_token = prediction.addressToken;

        if (!description) return null;

        const isSelected = selectedIndex === index;

        return (
          <ListItem
            id="autocomplete-options"
            key={place_id || address_token}
            role="option"
            aria-selected={isSelected || undefined}
            overflow={'hidden'}
          >
            <PressableBox
              zIndex={2}
              justifyContent="flex-start"
              onMouseDown={(e) => e.preventDefault()}
              analyticsName=""
              animation="accelerateDecelerateFastest"
              aria-label={t(
                'addressConfirmationModal.labels.addressPredictions',
                'Address predictions',
              )}
              height="100%"
              width="100%"
              backgroundColor={isSelected ? '$backgroundStateHoverPrimary' : '$transparent'}
              hoverStyle={{
                backgroundColor: '$backgroundStateHoverPrimary',
              }}
              onPress={() => {
                onPredictionSelect(prediction);
              }}
              pressStyle={{
                scale: 0.98,
              }}
              padding={10}
            >
              <Text
                typography="$bodyXlarge"
                tag="span"
                textAlign="left"
                $smallerThanMD={{
                  typography: '$bodyMedium',
                }}
                px={10}
                py={2}
              >
                {description}
              </Text>
            </PressableBox>
          </ListItem>
        );
      })}
      {predictions.length < 5 && (
        <TextButton
          zIndex={2}
          onMouseDown={(e) => e.preventDefault()}
          label={t('homepage.titles.address', 'Enter address manually instead')}
          analyticsName="cosmos-address-input-manual-entry"
          type="button"
          onPress={() => {
            gateManualAddressVerification({
              forceRedirect: true,
            }).then((result) => {
              if (result.shouldRedirect && result.destination) {
                router.push({
                  pathname: result.destination,
                  query: {
                    ...(trackingKeywords != null && { trackingKeywords }),
                    ...(partnerReferralId != null && { partnerReferralId }),
                    ...(pageSetPath != null && { pageSetPath }),
                    ...(channel != null && { channel }),
                  },
                });
              }
            });
          }}
          width={'100%'}
          variant="primary"
          size="medium"
          px={20}
          paddingTop={10}
          paddingBottom={20}
        />
      )}
    </NovoListBox>
  );
};

const NovoAddressInput: React.FC<NovoAddressInputProps> = ({
  id,
  placeholder,
  disabled,
  hideLabel,
  prediction,
  pageSetPath,
  trackingKeywords,
  partnerReferralId,
  channel,
}) => {
  const {
    predictions,
    setPredictions,
    searchTerm,
    onChange,
    handlePredictionSelect,
    listBoxId = 'cb1-listbox',
  } = prediction;
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);
  const { isPredictionSelectLoading } = useAddressValidationContext();

  const onKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLButtonElement> = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const newSelectedIndex =
        selectedIndex === undefined || selectedIndex === predictions.length - 1
          ? 0
          : selectedIndex + 1;
      setSelectedIndex(newSelectedIndex);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newSelectedIndex =
        selectedIndex === undefined || selectedIndex === 0
          ? predictions.length - 1
          : selectedIndex - 1;
      setSelectedIndex(newSelectedIndex);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex !== undefined) handlePredictionSelect(predictions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setPredictions([]);
      setSelectedIndex(undefined);
    } else {
      setSelectedIndex(undefined);
    }
  };

  // Check if the new focused element is outside the container
  const handleContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsFocused(false);
    }
  };

  return (
    <Box
      className="od-address-input"
      onFocus={() => setIsFocused(true)}
      onBlur={handleContainerBlur}
    >
      <Box position={'relative'} zIndex={2} className="cta-input-form">
        <CustomFieldWrapper noValue={searchTerm === ''}>
          <Field
            label={hideLabel ? '' : 'Enter your home address'}
            input={{
              autoComplete: 'off',
              placeholder,
              disabled,
              id: id,
              onChange: onChange,
              onKeyDown: onKeyDown,
              value: searchTerm ?? '',
              type: 'text',
              role: 'combobox',
              'aria-autocomplete': 'list',
              'aria-expanded': predictions.length > 0 ? 'true' : 'false',
              'aria-controls': listBoxId,
              suffix: !isPredictionSelectLoading ? (
                isFocused ? (
                  <Icons.Search color="$contentAccentBrand" />
                ) : (
                  <Icons.Search />
                )
              ) : (
                <Box position="relative" px={5}>
                  <ButtonLoadingAnimation variant="secondary" size="xsmall" />
                </Box>
              ),
            }}
          />
        </CustomFieldWrapper>
      </Box>
      <Box position="relative">
        {searchTerm !== '' && isFocused && (
          <AutoCompletePredictions
            predictions={predictions}
            selectedIndex={selectedIndex}
            onPredictionSelect={handlePredictionSelect}
            listBoxId={listBoxId}
            pageSetPath={pageSetPath}
            trackingKeywords={trackingKeywords}
            partnerReferralId={partnerReferralId}
            channel={channel}
          />
        )}
      </Box>
    </Box>
  );
};

export default NovoAddressInput;
